import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Box, Button, Avatar, IconButton } from "@mui/material";
import logoEmpresa from "../media/logoempresa.png";
import { getopcinesMenu } from "../services/apis";

import TodayIcon from "@mui/icons-material/Today";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import LogoutIcon from "@mui/icons-material/Logout";

const iconMapping = {
  TodayIcon: <TodayIcon />,
  FolderSpecialIcon: <FolderSpecialIcon />,
};

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tokenusu, setTokenUsu] = useState("");
  const [opciones, setOpciones] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const aliveRef = useRef(true);
  const skipMenuRoutes = useMemo(() => ["/login"], []);

  // ✅ Sesión real (no dependas de DesRepresentante)
  const isLogged = useMemo(() => {
    return !!localStorage.getItem("accessToken") || !!localStorage.getItem("refreshToken");
  }, [location.pathname]); // se recalcula al cambiar ruta

  useEffect(() => {
    aliveRef.current = true;

    // ✅ Esto solo para mostrar inicial en el avatar (puede estar vacío)
    setTokenUsu(localStorage.getItem("DesRepresentante") || "");

    if (skipMenuRoutes.some((r) => location.pathname.startsWith(r))) {
      if (aliveRef.current) {
        setOpciones([]);
        setLoaded(true);
      }
      return () => {
        aliveRef.current = false;
      };
    }

    const cargarOpciones = async () => {
      try {
        setLoaded(false);

        const response = await getopcinesMenu();
        const data = response?.data ?? response;

        if (aliveRef.current) {
          setOpciones(Array.isArray(data) ? data : []);
          setLoaded(true);
        }
      } catch (error) {
        console.error("Error al cargar opciones AppBar:", error);

        const status = error?.response?.status || error?.status;

        if (status === 401) {
          localStorage.clear();
          if (aliveRef.current) {
            setOpciones([]);
            setLoaded(true);
            setTokenUsu("");
          }
          navigate("/login", { replace: true });
          return;
        }

        if (aliveRef.current) {
          setOpciones([]);
          setLoaded(true);
        }
      }
    };

    cargarOpciones();

    return () => {
      aliveRef.current = false;
    };
  }, [location.pathname, skipMenuRoutes, navigate]);

  const esEnlace = (texto) => {
    if (typeof texto !== "string" || !texto.trim()) return false;
    return /^(https?:\/\/|www\.)/i.test(texto.trim());
  };

  const cerrarSesionDirecta = () => {
    localStorage.clear();
    setTokenUsu("");
    navigate("/login", { replace: true });
  };

  return (
    <AppBar position="sticky" elevation={1} sx={{ width: "100%" }}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          minHeight: 64,
          px: 2,
          boxSizing: "border-box",
        }}
      >
        {/* LOGO IZQUIERDA */}
        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
          onClick={() => navigate("/")}
        >
          <img
            src={logoEmpresa}
            alt="Logo empresa"
            style={{ height: 40, width: "auto", maxWidth: "100%" }}
          />
        </Box>

        {/* OPCIONES CENTRADAS */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexGrow: 1,
            justifyContent: "center",
            overflowX: "auto",
            minWidth: 0,
            px: 2,
          }}
        >
          {loaded &&
            opciones
              .filter((opcion) => opcion?.tipo === 0)
              .map((opcion) => (
                <Button
                  key={opcion.id}
                  color="inherit"
                  startIcon={iconMapping[opcion.icon] || null}
                  onClick={() =>
                    esEnlace(opcion.url)
                      ? window.open(opcion.url, "_blank", "noopener,noreferrer")
                      : navigate(opcion.url)
                  }
                  sx={{ whiteSpace: "nowrap", flexShrink: 0, textTransform: "none" }}
                >
                  {opcion.nombre}
                </Button>
              ))}
        </Box>

        {/* AVATAR + LOGOUT DERECHA */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: "#1976d2" }}>
            {tokenusu ? tokenusu.charAt(0) : isLogged ? "U" : "?"}
          </Avatar>

          {/* ✅ Siempre visible si hay sesión real */}
          {isLogged && (
            <IconButton
              size="small"
              color="inherit"
              onClick={cerrarSesionDirecta}
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
              title="Cerrar sesión"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
